import {
    Box, Grid, Button, Typography, IconButton, Menu, MenuItem, Skeleton,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import React from 'react';
import type { MenuListItem } from '../../component/menu-list/menu-list-component';
import './scss/print-device.styles.scss'
import CommonInput from '../../component/form/common-input.component'
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import RadioButtonsGroup from '../../component/radio/radio.component';
import CheckBoxGroup from '../../component/checkbox/checkbox.component';
import { apiManage_rbp as apiManage } from '../../request';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import DialogComponent from '../../component/dialog/dialog.component';
import GlobalNotification from '../../component/common/snackbar';
import _ from 'lodash';
import i18n from '../../i18n';
import { KdsPrinter } from '../../models/kds-printer.entity';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import { Section } from '../../models/section.entity';
import ScrollTabs from '../../component/scroll-tabs.component';
import { SectionTable } from '../../models/section-table.entity';
import { PrintModuleAuxiliaryModeContext } from './print-module-auxiliary-mode-context';
import {
    PrintDetailDeletePrinterButton,
    PrintDetailHeadActions,
    PrintDetailSaveButton,
} from './components/print-detail-head-buttons';

enum PrintDeviceDialogType {
    ADD_PRINT_DEVICE = 'ADD_PRINT_DEVICE',
}

type PrinterGlobalSettings = {
    feedLines: number;
    printMode: 'Drive' | 'ImageWithZpl' | 'Direct';
    paperSize: number;
    marginX: number;
    dpi: number;
    scale: number;
    fontScale: number;
}

/** 打印机详情：标题 + 说明 + 控件（与名称行布局一致） */
function PrintDeviceField(props: { label: React.ReactNode; hint: React.ReactNode; children: React.ReactNode }) {
    const { auxiliaryModeEnabled } = React.useContext(PrintModuleAuxiliaryModeContext);
    return (
        <Box className='print-device-page__field'>
            <Typography component='div' className='print-device-page__field-label'>
                {props.label}
            </Typography>
            {auxiliaryModeEnabled ? (
                <Typography component='div' className='print-device-page__field-hint'>
                    {props.hint}
                </Typography>
            ) : null}
            {props.children}
        </Box>
    );
}

function PrintDeviceSectionHint(props: { children: React.ReactNode }) {
    const { auxiliaryModeEnabled } = React.useContext(PrintModuleAuxiliaryModeContext);
    if (!auxiliaryModeEnabled) {
        return null;
    }
    return (
        <Typography component='div' className='print-device-page__section-hint'>
            {props.children}
        </Typography>
    );
}

/** 与 `PrintDeviceField` 内说明行同样式，用于非 Field 布局下的说明 */
function PrintDeviceAuxFieldHint(props: { children: React.ReactNode }) {
    const { auxiliaryModeEnabled } = React.useContext(PrintModuleAuxiliaryModeContext);
    if (!auxiliaryModeEnabled) {
        return null;
    }
    return (
        <Typography component='div' className='print-device-page__field-hint'>
            {props.children}
        </Typography>
    );
}

class PrintDevicePage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.onSave = _.debounce(this.onSave, 500, {
            leading: true,
            trailing: false
        }).bind(this) as any;
    }
    defaultPrinterGlobalSettings: PrinterGlobalSettings = {
        feedLines: 5,
        printMode: 'Direct',
        paperSize: 80,
        marginX: 8,
        dpi: 203,
        scale: 1,
        fontScale: 2.35,
    };
    public state: State = {
        // 所有打印机
        printGroup: [],
        // 选择的打印机
        activeMenu: '',
        // 打印机连接方式
        printMode: '0',
        // 数据位
        dataBits: '',
        // 停止位
        stopBit: '',
        // 校验类型
        checkType: '',
        // 数据流
        dataFlow: '',
        // 其他设置( 40111 切纸、40113 一菜一单、40112 打印转台\催单等提示信息、40115 打印后蜂鸣、40116 拆分打印、40117 标签打印机、堂食不打印、等取不打印、外送不打印、来取不打印)
        otherSetting: [],
        // 打印机名称
        printDeviceName: '',
        printDeviceId: '',
        printDeviceDialog: false,
        printDeviceDialogType: undefined,
        // 40104 串并口号
        comPort: 0,
        // 波特率
        baudRate: '',
        // 纸宽
        paperWidth: '',
        // 40106 IP或名称
        printerName: '',
        // 新建打印机设备
        addPrintDeviceName: '',
        // 打印头（1：h'eader；-1：null）
        printHeader: '',
        // 打印尾（1：trailer；-1：null）
        printTrailer: '',
        // 打印头组
        printHeadsOption: [],
        // 打印尾组
        printTrailersOption: [],
        // 是否部分桌台打印
        isTablePrint: false,
        // 桌台区域
        sectionList: [],
        // 选中的桌台列表
        selectTableList: [],
        // 打印规格设置（存储到 print_devices.printer_global_settings）
        printerGlobalSettings: this.defaultPrinterGlobalSettings,
        listItemMenuAnchor: null as HTMLElement | null,
        listItemMenuDevice: null as any,
        /** 桌台分区 Tab 当前下标（与 ScrollTabs 同步，用于工具栏作用当前分区） */
        tableSectionTabIndex: 0,
        pageLoading: true,
        saveLoading: false,
        /** 删除打印机：与打印头页一致使用 AlertDialog */
        deleteAlertOpen: false,
        deleteTarget: null as any,
        deleteSubmitting: false,
    }

    printIp: string = '';

    // *********************
    // Life Cycle Function
    // *********************
    async componentDidMount() {
        try {
            // 头/尾下拉选项、设备详情、桌台分区互不依赖，可并发（详情里 header/trailer 取值来自设备记录）
            await Promise.all([
                this.getPrintHead(),
                this.getPrintTrailers(),
                this.getPrinterDevice(),
                this.getSectionList(),
            ]);
        } finally {
            this.setState({ pageLoading: false });
        }
    }

    // *********************
    // Default Function
    // *********************

    /**
     * 获取打印头
     */
    async getPrintHead() {
        const res = await apiManage.getPrinterHead();
        if (!res?.error) {
            const printHeadsOption = res?.map((item: any) => {
                return {
                    label: item.descriptors_headers_name,
                    value: item.descriptors_headers_id,
                }
            })
            printHeadsOption.unshift({ label: i18n.t('print_setting_page_none_select_now'), value: '0' });
            this.setState({
                printHeadsOption
            })
        }
    }
    /**
     * 获取打印尾
     */
    async getPrintTrailers() {
        const res = await apiManage.getPrinterTrailers();
        if (!res?.error) {
            const printTrailersOption: any[] = res?.map((item: any) => {
                return {
                    label: item.descriptors_trailers_name,
                    value: `${item.descriptors_trailers_id}`,
                }
            })
            printTrailersOption.unshift({ label: i18n.t('print_setting_page_none_select_now'), value: 0 });
            this.setState({
                printTrailersOption
            })
        }
    }

    /**
     * 获取打印机设备
     * @param asyncDis 是否同步dis设置的打印机
     */
    async getPrinterDevice(asyncDis?: boolean) {
        const res = await apiManage.getPrinterDevice({});
        if (!res?.error && Array.isArray(res)) {
            const printdevice = res?.map((item: any) => {
                return {
                    title: item.print_device_name,
                    key: `${item.print_device_id}`,
                    dataSource: item
                }
            })
            const activeDeviceItem = res.find((item) => item.print_device_id === this.state.activeMenu);
            // 默认选中第一个
            const deviceItem = activeDeviceItem ? activeDeviceItem : res?.[0];
            this.setState({
                printGroup: printdevice,
                activeMenu: deviceItem?.print_device_id || '',
            })
            await this.getPrinterDeviceItem(deviceItem);
            asyncDis && this.sayncAllDisPrinter();
        }
    }
    /**
     * 获取单个设备
     */
    async getPrinterDeviceItem(item: any) {
        if (!item) return;
        /**
         * - 40111 切纸: print_table_number
         * - 40113 一菜一单: redirection_device_id
         * - 40112 打印转台\催单等提示信息: is_print_note
         * - 40115 打印后蜂鸣: beep
         * - 40116 拆分打印: split_print
         * - 40117 标签打印机: print_label
         * - 1-堂食不打印；2-等取不打印；4-外送不打印；8-来取不打印 : eat_type_skip 四个选项相加，未开启是0
         *   - eat_type_skip_dine 堂食不打印：1、5、9、7、11、13、14、15
         *   - eat_type_skip_wait 等取不打印：2、3、6、7、11、14、15
         *   - eat_type_skip_delivery 外送不打印：4、5、6、7、12、13、14、15
         *   - eat_type_skip_come 来取不打印：8、9、11、12、13、14、15
         */
        const otherSetting: string[] = [];
        if (item.eat_type_skip > 0) {
            if ([1, 5, 9, 7, 11, 13, 14, 15].includes(item.eat_type_skip)) {
                otherSetting.push('eat_type_skip_dine');
            }
            if ([2, 3, 6, 7, 11, 14, 15].includes(item.eat_type_skip)) {
                otherSetting.push('eat_type_skip_wait');
            }
            if ([4, 5, 6, 7, 12, 13, 14, 15].includes(item.eat_type_skip)) {
                otherSetting.push('eat_type_skip_delivery');
            }
            if ([8, 9, 11, 12, 13, 14, 15].includes(item.eat_type_skip)) {
                otherSetting.push('eat_type_skip_come');
            }
        }
        const arr = ['print_table_number', 'redirection_device_id', 'beep', 'split_print', 'print_label'];
        arr.forEach((key) => {

            if (key === 'print_table_number' || key === 'split_print' || key === 'print_label') {
                if (item[key]?.data?.[0] === 1) {
                    otherSetting.push(key);
                }
            } else if (item[key] !== 0) {
                otherSetting.push(key)
            }
        })
        this.printIp = item.printer_name || '';
        const chosenTables = item?.chosen_tables || JSON.stringify({ is_enable: 0, chosen_tables: [] });
        const { is_enable = 0, chosen_tables = [] } = JSON.parse(chosenTables);
        let printerGlobalSettings = _.cloneDeep(this.defaultPrinterGlobalSettings);
        try {
            const rawGlobalSettings = item?.printer_global_settings;
            const parsedSettings = typeof rawGlobalSettings === 'string'
                ? JSON.parse(rawGlobalSettings || '{}')
                : rawGlobalSettings;
            if (_.isObject(parsedSettings)) {
                printerGlobalSettings = {
                    ...printerGlobalSettings,
                    ...parsedSettings,
                };
            }
        } catch (error) { }
        this.setState({
            // 打印机连接方式0：串口；1：并口；3：网络接口；4：驱动程序；5：兼容模式；6：TCP透传
            printMode: `${item.check_info_print || 0}`,
            // 数据位
            dataBits: `${item.num_data_bit || 0}`,
            // 停止位
            stopBit: `${item.num_stop_bits || 0}`,
            // 校验类型（0：无；1：奇数；2：偶数）
            checkType: `${item.parity_type || 0}`,
            // 数据流（1：硬件；2：XON/OFF；3：无）
            dataFlow: `${item.flow_control || 0}`,
            // 其他设置( 40111 切纸、40113 一菜一单、40112 打印转台\催单等提示信息、40115 打印后蜂鸣、40116 拆分打印、40117 标签打印机、堂食不打印、等取不打印、外送不打印、来取不打印)
            otherSetting: otherSetting,
            // 打印机名称
            printDeviceName: item.print_device_name,
            printDeviceId: item.print_device_id,
            activeMenu: item.print_device_id,
            // 40104 串并口号
            comPort: item.com_port,
            // 波特率
            baudRate: item.baud_rate || '',
            // 纸宽
            paperWidth: item.paper_width || '',
            // 40106 IP或名称
            printerName: item.printer_name,
            // 打印头
            printHeader: `${item.header || ''}`,
            // 打印尾
            printTrailer: `${item.trailer || ''}`,
            // 桌台选择打印
            isTablePrint: is_enable === 1,
            // 选中的桌台
            selectTableList: chosen_tables || [],
            // 打印规格设置
            printerGlobalSettings,
            tableSectionTabIndex: 0,
        })
    }

    /**
     * 桌台 Tab 栏右侧：全选 / 取消全选（仅作用于当前分区，与 Tabs 同一行）
     */
    private renderTableTabBarEnd() {
        const { sectionList, tableSectionTabIndex } = this.state;
        const maxIdx = Math.max(0, sectionList.length - 1);
        const safeIdx = Math.min(Math.max(0, tableSectionTabIndex), maxIdx);
        const item = sectionList[safeIdx];
        const tables = item?.tables ?? [];
        if (tables.length === 0) {
            return null;
        }
        return (
            <Box className='print-device-page__table-toolbar print-device-page__table-toolbar--tab-bar'>
                <Button
                    size='small'
                    className='print-device-page__table-toolbar-btn'
                    onClick={() => {
                        this.allChecked(false, tables);
                    }}
                >
                    <Typography variant='subtitle1' component='span' className='print-device-page__table-toolbar-tab-label'>
                        {i18n.t('mop_setting_page_select_all')}
                    </Typography>
                </Button>
                <Box className='print-device-page__table-toolbar-divider' aria-hidden />
                <Button
                    size='small'
                    className='print-device-page__table-toolbar-btn'
                    onClick={() => {
                        this.allChecked(true, tables);
                    }}
                >
                    <Typography variant='subtitle1' component='span' className='print-device-page__table-toolbar-tab-label'>
                        {i18n.t('condiment_text_unselect_all')}
                    </Typography>
                </Button>
            </Box>
        );
    }

    private getPrinterListSubtitle(ds: any): string {
        const mode = `${ds?.check_info_print ?? 0}`;
        const modeLabelMap: Record<string, string> = {
            '0': i18n.t('print_setting_page_serial_port'),
            '1': i18n.t('print_setting_page_parallel_port'),
            '3': i18n.t('print_setting_page_net_connection'),
            '4': i18n.t('print_setting_page_device_drive_program'),
            '5': i18n.t('print_setting_page_compatibility_mode'),
            '6': i18n.t('print_setting_page_tcp_passthrough'),
        };
        const label = modeLabelMap[mode] || '';
        const name = (ds?.printer_name || '').trim();
        if (['0', '1'].includes(mode)) {
            const port = ds?.com_port;
            const tail = port != null && port !== '' ? `COM ${port}` : '';
            return tail ? `${label} • ${tail}` : label;
        }
        return name ? `${label} • ${name}` : label;
    }

    private closePrinterListMenu = () => {
        this.setState({ listItemMenuAnchor: null, listItemMenuDevice: null });
    };

    private openPrinterListMenu = (e: React.MouseEvent<HTMLElement>, ds: any) => {
        e.stopPropagation();
        this.setState({ listItemMenuAnchor: e.currentTarget, listItemMenuDevice: ds });
    };

    /**
     * 发送测试页（与原生协议页一致走 postPrintersTest）
     */
    sendTestPrint = async () => {
        const { printMode, printerName, activeMenu } = this.state;
        const ip = (this.printIp || printerName || '').trim();
        if (!activeMenu) {
            GlobalNotification.warning(i18n.t('print_device_page_send_test_need_printer'));
            return;
        }
        if (['0', '1'].includes(printMode)) {
            GlobalNotification.warning(i18n.t('print_device_page_send_test_serial_unsupported'));
            return;
        }
        if (!ip) {
            GlobalNotification.warning(i18n.t('print_device_page_send_test_need_address'));
            return;
        }
        const theinterface = `tcp://${ip}:9100`;
        const res = await apiManage.postPrintersTest({
            type: 'EPSON',
            theinterface,
            printMode: 0,
        });
        if (res?.error) {
            GlobalNotification.error(res?.result?.message || theinterface);
        } else {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        }
    };

    /**
     * 更新单台打印机的 printer_global_settings:
     * - 数值字段统一转 number，并限制最小值为 0
     * - `feedLines` / `paperSize` / `marginX` / `dpi` 按整数处理
     * - `scale` / `fontScale` 保留 2 位小数
     * - 非法数字（NaN）不更新 state，避免把输入过程写成异常值
     */
    updatePrinterGlobalSettings = (key: keyof PrinterGlobalSettings, value: string | PrinterGlobalSettings['printMode']) => {
        this.setState((prevState) => {
            if (key === 'printMode') {
                return {
                    printerGlobalSettings: {
                        ...prevState.printerGlobalSettings,
                        printMode: value as PrinterGlobalSettings['printMode'],
                    }
                };
            }
            const rawNumber = Number(value);
            if (Number.isNaN(rawNumber)) return null;
            // 整数项与浮点项分开归一化，与后端 JSON 字段类型约定一致
            const integerKeys = ['feedLines', 'paperSize', 'marginX', 'dpi'] as const;
            const isIntegerKey = integerKeys.some((integerKey) => integerKey === key);
            const parsedValue = isIntegerKey
                ? Math.max(0, Math.round(rawNumber))
                : Math.max(0, Number(rawNumber.toFixed(2)));
            return {
                printerGlobalSettings: {
                    ...prevState.printerGlobalSettings,
                    [key]: parsedValue
                }
            };
        });
    }


    /** 详情头「删除打印机」：AlertDialog 确认（与打印头设置一致） */
    openDeleteCurrentPrinterDialog = () => {
        const row = this.state.printGroup.find((p) => String(p.key) === String(this.state.activeMenu));
        if (!row?.dataSource) {
            return;
        }
        this.setState({ deleteAlertOpen: true, deleteTarget: row.dataSource });
    };

    /** 列表菜单 / 详情删除确认后提交 */
    confirmDeletePrinterDevice = async () => {
        const id = this.state.deleteTarget?.print_device_id;
        if (id == null) {
            return;
        }
        this.setState({ deleteSubmitting: true });
        try {
            await apiManage.delPrinterDevice([Number(id)]);
            this.setState({ deleteAlertOpen: false, deleteTarget: null });
            await this.getPrinterDevice(true);
        } finally {
            this.setState({ deleteSubmitting: false });
        }
    };

    /** 左侧列表「+」打开新建打印机弹窗 */
    openPrintDeviceAddDialog = () => {
        this.setState({
            printDeviceDialog: true,
            printDeviceDialogType: PrintDeviceDialogType.ADD_PRINT_DEVICE,
            addPrintDeviceName: '',
        });
    };

    dialogConfig(action: 'CONTENT' | 'TITLE' | 'FUN' = 'FUN') {
        const { printDeviceDialogType, } = this.state;
        let content = <div></div>;
        let fun = async () => { };
        let title = '';
        switch (printDeviceDialogType) {
            case 'ADD_PRINT_DEVICE':
                if (action === 'CONTENT') {
                    content = (
                        <FormFieldCell labelKey='print_setting_page_printer_name'>
                            <CommonInput
                                variant='outlined'
                                fullWidth
                                hiddenLabel
                                regularType='required'
                                value={this.state.addPrintDeviceName}
                                onChange={(e) => {
                                    this.setState({ addPrintDeviceName: e.target.value });
                                }}
                            />
                        </FormFieldCell>
                    )
                } else if (action === 'TITLE') {
                    title = i18n.t('print_setting_page_printer_info');
                } else {
                    fun = async () => {
                        if (_.trim(this.state.addPrintDeviceName) === '') {
                            GlobalNotification.warning(i18n.t('staff_setting_page_enter_type_name'))
                            return;
                        }
                        if (this.state.addPrintDeviceName !== '') {
                            await apiManage.postPrinterDevice({ print_device_name: this.state.addPrintDeviceName })
                            this.setState({
                                printDeviceDialog: false,
                                printDeviceDialogType: undefined,
                            })
                            this.getPrinterDevice();
                        }
                    };
                }
                break;

        }
        if (action === 'CONTENT') {
            return content;
        } else if (action === 'TITLE') {
            return title;
        } else if (action === 'FUN') {
            return fun;
        }
    }

    /**
     * 保存
     */
    async onSave() {
        const { activeMenu, printDeviceName, printMode, dataBits, stopBit, checkType, dataFlow,
            otherSetting, comPort, baudRate, paperWidth, printHeader, printTrailer, isTablePrint, selectTableList, printerGlobalSettings } = this.state;
        // 名称必填
        if (!_.trim(printDeviceName)) {
            GlobalNotification.error(i18n.t('print_setting_page_printer_type_printer_device_name'));
            return;
        }
        this.setState({ saveLoading: true });
        /**
         * eat_type_skip
         * - 1-堂食不打印；2-等取不打印；4-外送不打印；8-来取不打印 : eat_type_skip 四个选项相加，未开启是0
         *   - eat_type_skip_dine 堂食不打印：1、5、9、7、11、13、14、15
         *   - eat_type_skip_wait 等取不打印：2、3、6、7、11、14、15
         *   - eat_type_skip_delivery 外送不打印：4、5、6、7、12、13、14、15
         *   - eat_type_skip_come 来取不打印：8、9、11、12、13、14、15
         */
        let eat_type_skip = 0;
        if (otherSetting) {
            if (otherSetting.includes('eat_type_skip_dine')) {
                eat_type_skip += 1;
            }
            if (otherSetting.includes('eat_type_skip_wait')) {
                eat_type_skip += 2;
            }
            if (otherSetting.includes('eat_type_skip_delivery')) {
                eat_type_skip += 4;
            }
            if (otherSetting.includes('eat_type_skip_come')) {
                eat_type_skip += 8;
            }
        }

        try {
            const res = await apiManage.patchPrinterDevice(activeMenu, {
                // 名称
                print_device_name: printDeviceName,
                // 打印头
                header: Number(printHeader),
                // 打印尾
                trailer: Number(printTrailer),
                // 40103 连接方式 0串口 1并口 3网络 4驱动 5兼容 6TCP透传
                check_info_print: Number(printMode),
                // 40107 数据位
                num_data_bit: Number(dataBits),
                // 40108 停止位
                num_stop_bits: Number(stopBit),
                // 40109 校验类型
                parity_type: Number(checkType),
                // 40110 数据流
                flow_control: Number(dataFlow),
                // 40104 串/并口号
                com_port: comPort,
                // 40102 波特率
                baud_rate: baudRate,
                // 40113 纸宽
                paper_width: paperWidth,
                // 40106 IP或名称
                printer_name: this.printIp,
                // 40111 切纸 -
                print_table_number: otherSetting.includes('print_table_number') ? 1 : 0,
                // 40112 打印转台\催单等提示信息
                // is_print_note: otherSetting.includes('is_print_note') ? 1 : 0,
                // 40113 一菜一单
                redirection_device_id: otherSetting.includes('redirection_device_id') ? 1 : 0,
                // 40115 打印后蜂鸣
                beep: otherSetting.includes('beep') ? 1 : 0,
                // 40116 拆分打印 -
                split_print: otherSetting.includes('split_print') ? 1 : 0,
                // 40117 标签打印机 -
                print_label: otherSetting.includes('print_label') ? 1 : 0,
                //  1-堂食不打印；2-等取不打印；4-外送不打印；8-来取不打印 : eat_type_skip 四个选项相加，未开启是0
                eat_type_skip,
                // 桌台选择打印
                chosen_tables: JSON.stringify({ is_enable: isTablePrint ? 1 : 0, chosen_tables: selectTableList }),
                // 打印规格设置（每个打印机维度）
                printer_global_settings: JSON.stringify(printerGlobalSettings),
            });
            if (!res?.error) {
                this.getPrinterDevice(true);
                GlobalNotification.success(i18n.t('google_text_edit_succeed'));
            }
        } finally {
            this.setState({ saveLoading: false });
        }
    }

    /**
     * 同步dis设置的打印机
     */
    async sayncAllDisPrinter() {
        // 获取dis保存的打印机 为数组 
        const api91 = await apiManage.getSettingById(91);
        const savePrinters: KdsPrinter[] = _.get(api91, '[0].value', []);
        const newDisPrinter: KdsPrinter[] = [];
        if (Array.isArray(savePrinters) && savePrinters.length > 0) {
            savePrinters.forEach(item => {
                const current = this.state.printGroup.find(temp => Number(temp.key) === item.print_device_id);
                if (current) {
                    item.print_device_name = current?.title || '';
                    // item.printer_name = current.dataSource?.check_info_print === 3 ? current.dataSource?.printer_name || '' : '';
                    item.printer_name = current.dataSource?.printer_name || '';
                    newDisPrinter.push(item)
                }
            })
            await apiManage.patchSettingByIdByArr(91, newDisPrinter);
        }
    }

    /**
     * 获取桌台区域列表
     * @returns 
     */
    getSectionList = async () => {
        const result: any = await apiManage.get_res_tables({ with_section: true });
        if (!result?.error) {
            const takeaway = {
                rvc_center_id: -1,
                rvc_center_name: `${i18n.t('google_text_orderdelivery')}`,
                tables: [{
                    table_id: -1,
                    table_name: `${i18n.t('google_text_orderdelivery')}`,
                }],
            }
            result.unshift(takeaway);
            this.setState({
                sectionList: _.isArray(result) ? result : []
            })
        }
    }

    getTabComponent = () => {
        const { selectTableList, sectionList } = this.state;
        return sectionList.map((item) => {
            const { rvc_center_id = '-1', rvc_center_name = '', tables = [] } = item;
            return {
                id: `${rvc_center_id}`,
                label: rvc_center_name,
                component: (
                    <Box className='print-device-page__table-panel'>
                        <Box className='print-device-page__table-grid'>
                            {tables.map((table) => {
                                const tid = table.table_id || -1;
                                const selected = selectTableList.includes(tid);
                                return (
                                    <Box
                                        key={`${rvc_center_id}-${tid}`}
                                        role='button'
                                        tabIndex={0}
                                        className={
                                            selected
                                                ? 'print-device-page__table-chip print-device-page__table-chip--selected'
                                                : 'print-device-page__table-chip'
                                        }
                                        onClick={() => {
                                            this.onCheck(tid, !selected);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                this.onCheck(tid, !selected);
                                            }
                                        }}
                                    >
                                        <Typography component='span' className='print-device-page__table-chip-label'>
                                            {table.table_name || ''}
                                        </Typography>
                                        {selected ? (
                                            <CheckCircleOutline className='print-device-page__table-chip-icon' fontSize='small' />
                                        ) : null}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ),
            };
        });
    }

    /**
     * 区域全选/反选
     */
    allChecked = (isAll: boolean, tableList: SectionTable[]) => {
        let selectList = _.cloneDeep(this.state.selectTableList);
        const ids = tableList.map(item => item.table_id || -1);
        // 反选
        if (isAll) {
            selectList = selectList.filter(id => !ids.includes(id))
        } else {
            ids.forEach(id => {
                if (!selectList.includes(id)) {
                    selectList.push(id);
                }
            })
        }
        this.setState({
            selectTableList: selectList
        })
    }

    /**
     * 多选
     */
    onCheck = (value: number, checked: boolean) => {
        let newValue = _.clone(this.state.selectTableList);
        if (checked) {
            newValue.push(value);
        } else {
            newValue = newValue.filter((n) => n !== value);
        }
        this.setState({
            selectTableList: newValue
        })
    }

    // *********************
    // View
    // *********************

    render() {
        const { printGroup, activeMenu, printMode, dataBits, stopBit, checkType, dataFlow, otherSetting, printDeviceDialog,
            printDeviceName, comPort, baudRate, paperWidth, printerName, printHeader, printTrailer, printHeadsOption, printTrailersOption,
            isTablePrint, printerGlobalSettings, listItemMenuAnchor, listItemMenuDevice,
            pageLoading, saveLoading, deleteAlertOpen, deleteSubmitting,
        } = this.state;
        if (pageLoading) {
            return <PrintDevicePageSkeleton />;
        }
        /** 设计稿未逐条给出的字段保留原有「保存后写入配置」类说明 */
        const genericFieldHint = i18n.t('print_native_protocol_page_param_hint');
        // 打印机连接方式 0：串口；1：并口；3：网络接口；4：驱动程序；5：兼容模式；6：TCP透传
        const printModeOption = [{ label: i18n.t('print_setting_page_serial_port'), value: '0' }, { label: i18n.t('print_setting_page_parallel_port'), value: '1' },
        { label: i18n.t('print_setting_page_net_connection'), value: '3' }, { label: i18n.t('print_setting_page_device_drive_program'), value: '4' }, { label: i18n.t('print_setting_page_compatibility_mode'), value: '5' }, { label: i18n.t('print_setting_page_tcp_passthrough'), value: '6' }];
        // 波特率
        const baudRateOption = [{ label: '1200', value: 1200 }, { label: '2400', value: 2400 },
        { label: '4800', value: 4800 }, { label: '9600', value: 9600 }, { label: '14400', value: 14400 },
        { label: '19200', value: 19200 }, { label: '28800', value: 28800 }, { label: '38400', value: 38400 },
        { label: '57600', value: 57600 }, { label: '115200', value: 115200 }];
        // 纸宽 40 58 76 80
        const paperWidthOption = [{ label: '40', value: 40 }, { label: '58', value: 58 }, { label: '76', value: 76 }, { label: '80', value: 80 },];
        // 数据位
        const dataBitsOption = [{ label: `7${i18n.t('print_setting_page_bit')}`, value: '7' }, { label: `8${i18n.t('print_setting_page_bit')}`, value: '8' }];
        // 停止位
        const stopBitOption = [{ label: `1${i18n.t('print_setting_page_bit')}`, value: '0' }, { label: `1.5${i18n.t('print_setting_page_bit')}`, value: '1' }, { label: `2${i18n.t('print_setting_page_bit')}`, value: '2' }];
        // 校验类型
        const checkTypeOption = [{ label: i18n.t('print_setting_page_none'), value: '0' }, { label: i18n.t('print_setting_page_odd'), value: '1' }, { label: i18n.t('print_setting_page_even'), value: '2' }];
        //  数据流（1：硬件；2：XON/OFF；3：无）
        const dataFlowOption = [{ label: 'XON/OFF', value: '2' }, { label: i18n.t('print_setting_page_hardware'), value: '1' }, { label: i18n.t('print_setting_page_none0'), value: '3' }];
        // 其他设置( 40111 切纸、40113 一菜一单、40112 打印转台\催单等提示信息、40115 打印后蜂鸣、40116 拆分打印、40117 标签打印机、堂食不打印、等取不打印、外送不打印、来取不打印)
        const otherOptions = [{ label: i18n.t('print_setting_page_print_table_number'), value: 'print_table_number' }, { label: i18n.t('print_setting_page_redirection_device_id_40113'), value: 'redirection_device_id' },
        { label: i18n.t('print_setting_page_printer_option_40115'), value: 'beep' }, { label: i18n.t('print_setting_page_printer_option_40116'), value: 'split_print' }, { label: i18n.t('print_setting_page_printer_option_40117'), value: 'print_label' }, { label: i18n.t('print_setting_page_eat_type_skip_dine'), value: 'eat_type_skip_dine' },
        { label: i18n.t('print_setting_page_eat_type_skip_wait'), value: 'eat_type_skip_wait' }, { label: i18n.t('print_setting_page_eat_type_skip_delivery'), value: 'eat_type_skip_delivery' }, { label: i18n.t('print_setting_page_eat_type_skip_come'), value: 'eat_type_skip_come' }];
        const isShowPrinterGlobalSettings = ['5', '6'].includes(printMode);
        // const printerGlobalModeOptions = [
        //     { label: 'Drive', value: 'Drive' as const },
        //     { label: 'ImageWithZpl', value: 'ImageWithZpl' as const },
        //     { label: 'Direct', value: 'Direct' as const },
        // ];
        const printerGlobalFields: Array<{
            key: keyof PrinterGlobalSettings;
            label: string;
            type?: 'number';
            step?: number;
            select?: boolean;
            items?: Array<{ label: string, value: PrinterGlobalSettings['printMode'] }>;
        }> = [
                { key: 'paperSize', label: i18n.t('print_basic_setting_page_print_spec_paper_size'), type: 'number', step: 1 },
                { key: 'marginX', label: i18n.t('print_basic_setting_page_print_spec_margin_x'), type: 'number', step: 1 },
                { key: 'dpi', label: i18n.t('print_basic_setting_page_print_spec_dpi'), type: 'number', step: 1 },
                { key: 'scale', label: i18n.t('print_basic_setting_page_print_spec_scale'), type: 'number', step: 0.01 },
                { key: 'fontScale', label: i18n.t('print_basic_setting_page_print_spec_font_scale'), type: 'number', step: 0.01 },
                // { key: 'printMode', label: i18n.t('print_basic_setting_page_print_spec_mode'), select: true, items: printerGlobalModeOptions },
                { key: 'feedLines', label: i18n.t('print_basic_setting_page_print_spec_feed_lines'), type: 'number', step: 1 },
            ];

        return (
            <Box className='print-device-page print-device-page--shell'>
                <Grid className='print-device-page__layout' container spacing={3}>
                    <Grid item className='print-device-page__list-column'>
                        <Box className='print-device-page__list-card'>
                            <Box className='print-device-page__list-head'>
                                <Typography component='div' className='print-device-page__list-title'>
                                    {i18n.t('print_device_page_printer_list_title')}
                                </Typography>
                                <IconButton
                                    size='small'
                                    className='print-device-page__list-add'
                                    onClick={() => {
                                        this.openPrintDeviceAddDialog();
                                    }}
                                    aria-label={i18n.t('google_text_newly_increased')}
                                >
                                    <Add />
                                </IconButton>
                            </Box>
                            <Box className='print-device-page__list-body'>
                                {printGroup.map((row) => {
                                    const isActive = `${activeMenu}` === `${row.key}`;
                                    return (
                                        <Box
                                            key={row.key}
                                            className={
                                                isActive
                                                    ? 'print-device-page__list-item print-device-page__list-item--active'
                                                    : 'print-device-page__list-item'
                                            }
                                            onClick={() => {
                                                this.getPrinterDeviceItem(row.dataSource);
                                            }}
                                        >
                                            <Box className='print-device-page__list-item-text'>
                                                <Typography component='div' className='print-device-page__list-item-name'>
                                                    {row.title}
                                                </Typography>
                                                <Typography component='div' className='print-device-page__list-item-sub'>
                                                    {this.getPrinterListSubtitle(row.dataSource)}
                                                </Typography>
                                            </Box>
                                            {/* <IconButton
                                                size='small'
                                                className='print-device-page__list-item-more'
                                                onClick={(e) => {
                                                    this.openPrinterListMenu(e, row.dataSource);
                                                }}
                                                aria-label={i18n.t('google_text_public_button_delete')}
                                            >
                                                <MoreVert />
                                            </IconButton> */}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Grid>
                    {_.isEmpty(printGroup) ? null : (
                        <Grid item xs className='print-device-page__detail-column' minWidth={0}>
                            <Box className='print-device-page__detail-card'>
                                <Box className='print-device-page__detail-head'>
                                    <Typography component='div' className='print-device-page__detail-title'>
                                        {i18n.t('print_device_page_detail_title')}
                                    </Typography>
                                    <PrintDetailHeadActions className='print-device-page__detail-actions'>
                                        <PrintDetailDeletePrinterButton
                                            className='print-device-page__btn-delete-printer'
                                            label={i18n.t('print_detail_delete_printer')}
                                            disabled={!activeMenu}
                                            onClick={this.openDeleteCurrentPrinterDialog}
                                        />
                                        <PrintDetailSaveButton
                                            className='print-device-page__btn-save'
                                            loading={saveLoading}
                                            onClick={() => {
                                                void this.onSave();
                                            }}
                                        >
                                            {i18n.t('job_settings_save')}
                                        </PrintDetailSaveButton>
                                    </PrintDetailHeadActions>
                                </Box>
                                <Box className='print-device-page__detail-body'>
                                    <Grid container spacing={2} className='print-device-page__row-three'>
                                        <Grid item xs={12} md={4}>
                                            <PrintDeviceField label={i18n.t('print_setting_page_printer_name')} hint={i18n.t('print_device_page_aux_printer_name_hint')}>
                                                <CommonInput
                                                    variant='outlined'
                                                    fullWidth
                                                    required
                                                    hiddenLabel
                                                    value={printDeviceName}
                                                    onChange={(e) => {
                                                        this.setState({ printDeviceName: e.target.value });
                                                    }}
                                                />
                                            </PrintDeviceField>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <PrintDeviceField label={i18n.t('print_setting_page_printHeader')} hint={i18n.t('print_device_page_aux_header_template_hint')}>
                                                <CommonInput
                                                    inputType='SELECT'
                                                    select
                                                    variant='outlined'
                                                    fullWidth
                                                    hiddenLabel
                                                    value={printHeader}
                                                    items={printHeadsOption}
                                                    onChange={(e) => {
                                                        this.setState({ printHeader: e.target.value });
                                                    }}
                                                />
                                            </PrintDeviceField>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <PrintDeviceField label={i18n.t('print_setting_page_printTrailer')} hint={i18n.t('print_device_page_aux_footer_template_hint')}>
                                                <CommonInput
                                                    inputType='SELECT'
                                                    select
                                                    variant='outlined'
                                                    fullWidth
                                                    hiddenLabel
                                                    value={printTrailer}
                                                    items={printTrailersOption}
                                                    onChange={(e) => {
                                                        this.setState({ printTrailer: e.target.value });
                                                    }}
                                                />
                                            </PrintDeviceField>
                                        </Grid>
                                    </Grid>
                                    <Box className='print-device-page__section'>
                                        <Typography component='div' className='print-device-page__section-title'>
                                            {i18n.t('print_device_page_connection_settings')}
                                        </Typography>
                                        <PrintDeviceSectionHint>{i18n.t('print_device_page_aux_connection_section_hint')}</PrintDeviceSectionHint>
                                        <RadioButtonsGroup
                                            className='print-device-page__connection-radios'
                                            radioGroup={printModeOption}
                                            value={printMode}
                                            handleChange={(value) => {
                                                this.setState({ printMode: value });
                                            }}
                                            row
                                        />
                                    </Box>
                                    <Box className='print-device-page__connection-fields'>
                                        <Grid container spacing={2}>
                                            {['0', '1'].includes(printMode) && (
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <PrintDeviceField label={i18n.t('print_setting_page_40104_port')} hint={genericFieldHint}>
                                                        <CommonInput
                                                            variant='outlined'
                                                            fullWidth
                                                            hiddenLabel
                                                            value={comPort}
                                                            onChange={(e) => {
                                                                const value = Number(e.target.value);
                                                                if (!isNaN(value) || e.target.value === '') {
                                                                    this.setState({ comPort: e.target.value === '' ? undefined : value });
                                                                }
                                                            }}
                                                        />
                                                    </PrintDeviceField>
                                                </Grid>
                                            )}
                                            {printMode === '0' && (
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <PrintDeviceField label={i18n.t('print_setting_page_40102_bit_rate')} hint={genericFieldHint}>
                                                        <CommonInput
                                                            inputType='SELECT'
                                                            select
                                                            variant='outlined'
                                                            fullWidth
                                                            hiddenLabel
                                                            value={baudRate}
                                                            items={baudRateOption}
                                                            onChange={(e) => {
                                                                this.setState({ baudRate: e.target.value });
                                                            }}
                                                        />
                                                    </PrintDeviceField>
                                                </Grid>
                                            )}
                                            {!['0', '1'].includes(printMode) && (
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <PrintDeviceField label={i18n.t('print_device_page_ip_address_label')} hint={i18n.t('print_device_page_aux_ip_hint')}>
                                                        <CommonInput
                                                            variant='outlined'
                                                            fullWidth
                                                            hiddenLabel
                                                            value={printerName}
                                                            placeholder={i18n.t('print_setting_page_40106_ip_or_name')}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                this.printIp = v;
                                                                this.setState({ printerName: v });
                                                            }}
                                                        />
                                                    </PrintDeviceField>
                                                </Grid>
                                            )}
                                            <Grid item xs={12} sm={6} md={4}>
                                                <PrintDeviceField label={i18n.t('print_setting_page_40113_paper_width')} hint={genericFieldHint}>
                                                    <CommonInput
                                                        inputType='SELECT'
                                                        select
                                                        variant='outlined'
                                                        fullWidth
                                                        hiddenLabel
                                                        value={paperWidth}
                                                        items={paperWidthOption}
                                                        onChange={(e) => {
                                                            this.setState({ paperWidth: e.target.value });
                                                        }}
                                                    />
                                                </PrintDeviceField>
                                            </Grid>
                                        </Grid>
                                        {printMode === '0' && (
                                            <Grid container spacing={2} className='print-device-page__serial-extras'>
                                                <Grid item xs={12} md={6}>
                                                    <PrintDeviceField label={i18n.t('print_setting_page_40107_data_bit')} hint={genericFieldHint}>
                                                        <RadioButtonsGroup
                                                            radioGroup={dataBitsOption}
                                                            value={dataBits}
                                                            handleChange={(value) => {
                                                                this.setState({ dataBits: value });
                                                            }}
                                                            row
                                                        />
                                                    </PrintDeviceField>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <PrintDeviceField label={i18n.t('print_setting_page_40108_stop_bit')} hint={genericFieldHint}>
                                                        <RadioButtonsGroup
                                                            radioGroup={stopBitOption}
                                                            value={stopBit}
                                                            handleChange={(value) => {
                                                                this.setState({ stopBit: value });
                                                            }}
                                                            row
                                                        />
                                                    </PrintDeviceField>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <PrintDeviceField label={i18n.t('print_setting_page_40109_check_type')} hint={genericFieldHint}>
                                                        <RadioButtonsGroup
                                                            radioGroup={checkTypeOption}
                                                            value={checkType}
                                                            handleChange={(value) => {
                                                                this.setState({ checkType: value });
                                                            }}
                                                            row
                                                        />
                                                    </PrintDeviceField>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <PrintDeviceField label={i18n.t('print_setting_page_40110_data_stream')} hint={genericFieldHint}>
                                                        <RadioButtonsGroup
                                                            radioGroup={dataFlowOption}
                                                            value={dataFlow}
                                                            handleChange={(value) => {
                                                                this.setState({ dataFlow: value });
                                                            }}
                                                            row
                                                        />
                                                    </PrintDeviceField>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Box>
                                    {isShowPrinterGlobalSettings && (
                                        <Grid container spacing={2} className='print-device-page__global-spec'>
                                            {printerGlobalFields.map((field) => (
                                                <Grid item xs={12} md={4} key={field.key}>
                                                    <PrintDeviceField label={field.label} hint={genericFieldHint}>
                                                        <CommonInput
                                                            variant='outlined'
                                                            fullWidth
                                                            hiddenLabel
                                                            value={printerGlobalSettings[field.key]}
                                                            type={field.type}
                                                            select={field.select}
                                                            items={field.items}
                                                            onChange={(event: any) => {
                                                                this.updatePrinterGlobalSettings(field.key, event.target.value);
                                                            }}
                                                            InputProps={field.type === 'number' ? { inputProps: { min: 0, step: field.step } } : undefined}
                                                        />
                                                    </PrintDeviceField>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                    <Box className='print-device-page__section print-device-page__checkbox-section'>
                                        <Typography component='div' className='print-device-page__section-title'>
                                            {i18n.t('print_device_page_feature_options')}
                                        </Typography>
                                        <PrintDeviceSectionHint>{i18n.t('print_device_page_aux_feature_section_hint')}</PrintDeviceSectionHint>
                                        <Box className='print-device-page__checkbox-wrap'>
                                            <CheckBoxGroup
                                                checkBoxGroup={otherOptions}
                                                handleChange={(value) => {
                                                    this.setState({ otherSetting: value });
                                                }}
                                                autoSelected={(key: string, value: string[]) => {
                                                    if (key === 'split_print' && value?.includes('split_print') && !value?.includes('redirection_device_id')) {
                                                        value.push('redirection_device_id');
                                                    }
                                                    return value;
                                                }}
                                                value={otherSetting}
                                            />
                                        </Box>
                                    </Box>
                                    <Box className='print-device-page__table-print-head'>
                                        <Box className='print-device-page__table-print-titles'>
                                            <Typography component='div' className='print-device-page__field-label'>
                                                {i18n.t('print_setting_page_select_print_table')}
                                            </Typography>
                                            <PrintDeviceAuxFieldHint>
                                                {i18n.t('print_device_page_table_print_hint')}
                                            </PrintDeviceAuxFieldHint>
                                        </Box>
                                        <IOSSwitch
                                            checked={isTablePrint}
                                            onChange={() => {
                                                this.setState({ isTablePrint: !isTablePrint });
                                            }}
                                        />
                                    </Box>
                                    {isTablePrint && (
                                        <ScrollTabs
                                            className='print-device-page__scroll-tabs'
                                            key={activeMenu}
                                            defaultValue={0}
                                            tabs={this.getTabComponent()}
                                            tabsClickEvent={(idx) => {
                                                this.setState({ tableSectionTabIndex: idx });
                                            }}
                                            tabBarEnd={this.renderTableTabBarEnd()}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
                <Menu
                    anchorEl={listItemMenuAnchor}
                    open={Boolean(listItemMenuAnchor)}
                    onClose={this.closePrinterListMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem
                        onClick={() => {
                            this.setState({ deleteAlertOpen: true, deleteTarget: listItemMenuDevice });
                            this.closePrinterListMenu();
                        }}
                    >
                        {i18n.t('google_text_public_button_delete')}
                    </MenuItem>
                </Menu>
                <AlertDialog
                    open={deleteAlertOpen}
                    variant="delete"
                    title={i18n.t('dinner_table_page_confirm_delete')}
                    desc={i18n.t('dinner_table_page_whether_delete')}
                    size='xs'
                    confirmLoading={deleteSubmitting}
                    onCancel={() => {
                        this.setState({ deleteAlertOpen: false, deleteTarget: null });
                    }}
                    onConfirm={() => void this.confirmDeletePrinterDevice()}
                />
                <DialogComponent
                    open={printDeviceDialog}
                    fullWidth={false}
                    onClose={() => { this.setState({ printDeviceDialog: false }) }}
                    defaultHeaderProps={{
                        title: this.dialogConfig('TITLE') as string,
                        onClose: () => { this.setState({ printDeviceDialog: false }) }
                    }}
                    footer={
                        <div>
                            <Button
                                color='primary'
                                variant='outlined'
                                onClick={() => {
                                    this.setState({ printDeviceDialog: false })
                                }}
                            >
                                {i18n.t('google_text_public_button_cancel')}
                            </Button>
                            <Box marginLeft={4} display="inline-block">
                                <Button
                                    color='primary'
                                    variant='contained'
                                    onClick={async () => {
                                        const fun = this.dialogConfig();
                                        if (typeof fun === 'function') {
                                            await fun();
                                        }
                                    }}
                                >
                                    {i18n.t('google_text_confirm')}
                                </Button>
                            </Box>
                        </div>
                    }
                >
                    <Grid style={{ padding: '8px 8px' }}>
                        {this.dialogConfig('CONTENT') as React.ReactNode}
                    </Grid >
                </DialogComponent >
            </Box>
        )
    }
}

/**
 * 打印机设置 — 首屏骨架（与本页同文件，不单独拆文件）
 */
function PrintDevicePageSkeleton() {
    return (
        <Box className='print-device-page print-device-page--shell print-device-page--skeleton'>
            <Grid className='print-device-page__layout' container spacing={3}>
                <Grid item className='print-device-page__list-column'>
                    <Box className='print-device-page__list-card'>
                        <Box className='print-device-page__list-head'>
                            <Skeleton className='print-device-page__skeleton-title' variant='text' width={120} height={28} />
                            <Skeleton variant='circular' width={32} height={32} />
                        </Box>
                        <Box className='print-device-page__list-body print-device-page__skeleton-list'>
                            {[1, 2, 3, 4, 5, 6, 7].map((k) => (
                                <Skeleton key={k} className='print-device-page__skeleton-list-row' variant='rounded' height={72} />
                            ))}
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs className='print-device-page__detail-column' minWidth={0}>
                    <Box className='print-device-page__detail-card'>
                        <Box className='print-device-page__detail-head'>
                            <Skeleton variant='text' width={200} height={28} />
                            <Box display='flex' sx={{ gap: 3 }}>
                                <Skeleton variant='rounded' width={120} height={44} />
                                <Skeleton variant='rounded' width={140} height={44} />
                            </Box>
                        </Box>
                        <Box className='print-device-page__detail-body'>
                            <Grid container spacing={2}>
                                {[1, 2, 3].map((k) => (
                                    <Grid item xs={12} md={4} key={k}>
                                        <Skeleton variant='text' width='40%' height={20} />
                                        <Skeleton variant='text' width='70%' height={18} sx={{ mt: 0.5 }} />
                                        <Skeleton variant='rounded' width='100%' height={56} sx={{ mt: 1 }} />
                                    </Grid>
                                ))}
                            </Grid>
                            <Box sx={{ mt: 2 }}>
                                <Skeleton variant='text' width={100} height={22} />
                                <Skeleton variant='text' width='60%' height={18} sx={{ mt: 0.5 }} />
                                <Skeleton variant='rounded' height={48} sx={{ mt: 1.5, width: '100%', maxWidth: 640 }} />
                            </Box>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={6}>
                                    <Skeleton variant='rounded' height={56} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Skeleton variant='rounded' height={56} />
                                </Grid>
                            </Grid>
                            <Skeleton variant='rounded' height={100} sx={{ mt: 2, width: '100%' }} />
                            <Box display='flex' alignItems='center' justifyContent='space-between' sx={{ mt: 2, maxWidth: 480 }}>
                                <Box>
                                    <Skeleton variant='text' width={140} height={22} />
                                    <Skeleton variant='text' width={220} height={18} sx={{ mt: 0.5 }} />
                                </Box>
                                <Skeleton variant='rounded' width={52} height={32} />
                            </Box>
                            <Skeleton variant='rounded' height={200} sx={{ mt: 1, width: '100%' }} />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default PrintDevicePage

// *********************
// State & Props
// *********************

type State = {
    printGroup: MenuListItem[];
    activeMenu: string;
    printMode: string;
    dataBits: string;
    stopBit: string;
    checkType: string;
    dataFlow: string;
    otherSetting: string[];
    printDeviceDialog: boolean;
    printDeviceName: string;
    printDeviceDialogType: PrintDeviceDialogType | undefined;
    printDeviceId: string;
    comPort?: number;
    baudRate: string;
    paperWidth: string;
    printerName: string;
    addPrintDeviceName: string;
    printHeader: string;
    printTrailer: string;
    printHeadsOption: any[];
    printTrailersOption: any[];
    // 是否部分桌台打印
    isTablePrint: boolean,
    // 桌台区域
    sectionList: Section[],
    // 选中的桌台列表
    selectTableList: number[];
    // 打印规格设置（print_devices.printer_global_settings）
    printerGlobalSettings: PrinterGlobalSettings;
    listItemMenuAnchor: HTMLElement | null;
    listItemMenuDevice: any;
    tableSectionTabIndex: number;
    pageLoading: boolean;
    saveLoading: boolean;
    deleteAlertOpen: boolean;
    deleteTarget: any;
    deleteSubmitting: boolean;
}

type Props = {}

